/**
 * SCIA funnel events, published through Shopify.analytics.publish() for the customer events pipeline.
 * No Google tag lives here: a custom pixel (docs/analytics/custom-pixel.js) forwards the two events to GA4.
 *
 * scia_cta_click      a "Richiedi disponibilità" / "Richiedi un preventivo" click
 * scia_generate_lead  a contact form sent successfully (Shopify's form.posted_successfully?)
 *
 * Only non-personal data is read or sent: the type of request, the Experience name carried by the button and
 * the page path. Name, email, phone and message are never read.
 *
 * A click is published on the page where it happens: the navigation waits a moment, so the customer events
 * pipeline of this page (already running) can deliver it. Publishing it on the next page would be too early,
 * before the pixels of that page have subscribed, and the event would be lost.
 * A lead is published on the page that confirms the form, once that page's pixels are running; its data waits
 * in sessionStorage and is removed when published, so a refresh never sends it twice.
 * Add ?scia_debug=1 to log the published events in the console.
 */

const EVENT_CTA_CLICK = 'scia_cta_click';
const EVENT_GENERATE_LEAD = 'scia_generate_lead';
const PENDING_LEAD_KEY = 'scia:pending-lead';
const DEBUG_KEY = 'scia:analytics-debug';
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
const NAME_MAX_LENGTH = 120;
const PUBLISH_RETRY_MS = 500;
const PUBLISH_MAX_ATTEMPTS = 20;
/** Time given to the pipeline to hand a click to the pixels before the browser leaves the page. */
const NAVIGATION_DELAY_MS = 300;
/** Shopify loads each custom pixel in a sandbox iframe with this id prefix. */
const PIXEL_SANDBOX_SELECTOR = 'iframe[id^="web-pixel-sandbox"]';
const PIXEL_POLL_MS = 250;
const PIXEL_WAIT_MAX_MS = 10000;
/** Time given to a sandbox, once present, to run its code and subscribe. */
const PIXEL_SETTLE_MS = 1500;

/** Reason keyword → lead type. Order matters: "Candidatura Ski Life Coach" also contains "coach". */
const LEAD_TYPE_BY_KEYWORD = Object.freeze([
  ['candidatura', 'coach_application'],
  ['experience', 'experience'],
  ['ambassador', 'ambassador'],
  ['partner', 'partner'],
  ['coach', 'coach_request'],
]);
const LEAD_TYPE_FALLBACK = 'other';
const CTA_TYPES = Object.freeze(['request_availability', 'request_quote']);

/** @returns {Storage | null} sessionStorage, or null when it is blocked. */
function getStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** @returns {boolean} */
function isDebug() {
  const storage = getStorage();
  if (new URLSearchParams(window.location.search).get('scia_debug') === '1') storage?.setItem(DEBUG_KEY, '1');
  return storage?.getItem(DEBUG_KEY) === '1';
}

/**
 * @param {string} value
 * @returns {string} Plain text, trimmed and capped.
 */
function cleanName(value) {
  return value.trim().slice(0, NAME_MAX_LENGTH);
}

/**
 * @param {Record<string, string>} payload
 * @returns {Record<string, string>} A copy without empty values.
 */
function withoutEmpty(payload) {
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== ''));
}

/**
 * Publishes an event as soon as Shopify's analytics API is available; gives up quietly after a few seconds.
 * @param {string} name
 * @param {Record<string, string>} payload
 * @param {number} [attempt]
 */
function publish(name, payload, attempt = 1) {
  const publisher = window.Shopify?.analytics?.publish;
  if (typeof publisher === 'function') {
    publisher(name, payload);
    if (isDebug()) console.info('[scia-analytics]', name, JSON.stringify(payload));
    return;
  }
  if (attempt < PUBLISH_MAX_ATTEMPTS) {
    window.setTimeout(() => publish(name, payload, attempt + 1), PUBLISH_RETRY_MS);
  }
}

/**
 * @param {string} key
 * @param {Record<string, string>} payload
 */
function storePending(key, payload) {
  getStorage()?.setItem(key, JSON.stringify({ payload, storedAt: Date.now() }));
}

/** @param {string} key */
function clearPending(key) {
  getStorage()?.removeItem(key);
}

/**
 * Reads a pending payload and leaves it in place: it is removed only when it has been published.
 * @param {string} key
 * @returns {Record<string, string> | null} Null when missing, unreadable or too old.
 */
function readPending(key) {
  const raw = getStorage()?.getItem(key);
  if (!raw) return null;

  try {
    const entry = JSON.parse(raw);
    const isFresh = typeof entry?.storedAt === 'number' && Date.now() - entry.storedAt < PENDING_MAX_AGE_MS;
    return isFresh && entry.payload && typeof entry.payload === 'object' ? entry.payload : null;
  } catch {
    return null;
  }
}

/**
 * Resolves when the pixels of this page are running: the page has loaded, a pixel sandbox is in the document
 * and it has had time to subscribe. Without any sandbox (pixels blocked or consent denied) it resolves at
 * the timeout, so the caller is never left waiting.
 * @returns {Promise<void>}
 */
function whenPixelsReady() {
  const pageLoaded = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve(undefined);
    else window.addEventListener('load', () => resolve(undefined), { once: true });
  });

  return pageLoaded.then(
    () =>
      new Promise((resolve) => {
        const startedAt = Date.now();
        const check = () => {
          if (document.querySelector(PIXEL_SANDBOX_SELECTOR)) {
            window.setTimeout(resolve, PIXEL_SETTLE_MS);
          } else if (Date.now() - startedAt >= PIXEL_WAIT_MAX_MS) {
            resolve(undefined);
          } else {
            window.setTimeout(check, PIXEL_POLL_MS);
          }
        };
        check();
      })
  );
}

/** @returns {Record<string, string>} The request button labels of the current language, by CTA type. */
function readCtaLabels() {
  try {
    const config = JSON.parse(document.getElementById('scia-analytics-config')?.textContent ?? '{}');
    return config.cta_labels && typeof config.cta_labels === 'object' ? config.cta_labels : {};
  } catch {
    return {};
  }
}

/**
 * @param {HTMLAnchorElement} link
 * @param {Record<string, string>} labels
 * @returns {string} A CTA type, or an empty string when the link is not a request button.
 */
function readCtaType(link, labels) {
  const marked = link.dataset.sciaCta ?? '';
  if (CTA_TYPES.includes(marked)) return marked;

  const text = (link.textContent ?? '').replace(/[→\s]+/g, ' ').trim().toLowerCase();
  const match = CTA_TYPES.find((type) => typeof labels[type] === 'string' && labels[type].trim().toLowerCase() === text);
  return match ?? '';
}

/**
 * @param {MouseEvent} event
 * @param {HTMLAnchorElement} link
 * @returns {boolean} True when the click replaces this page: plain left click on a same-tab link.
 */
function leavesThisPage(event, link) {
  const opensElsewhere = link.target !== '' && link.target !== '_self';
  const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  return event.button === 0 && !modified && !opensElsewhere && !link.hasAttribute('download');
}

/** @param {Record<string, string>} labels */
function trackCtaClicks(labels) {
  let navigating = false;

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || event.defaultPrevented) return;

    const ctaType = readCtaType(link, labels);
    if (!ctaType) return;

    // A second click while the first one is on its way out must not publish again.
    if (navigating) {
      event.preventDefault();
      return;
    }

    publish(EVENT_CTA_CLICK, {
      ...withoutEmpty({
        cta_type: ctaType,
        experience_name: cleanName(link.dataset.sciaExperience ?? ''),
      }),
      page_path: window.location.pathname,
    });

    // A new tab or window leaves this page alive: nothing to wait for.
    if (!leavesThisPage(event, link)) return;

    // Same-tab navigation: hold it briefly so the pixels of this page receive the click, then go.
    event.preventDefault();
    navigating = true;
    window.setTimeout(() => window.location.assign(link.href), NAVIGATION_DELAY_MS);
  });

  // Coming back with the back button restores the page from cache: clicks must work again.
  window.addEventListener('pageshow', () => {
    navigating = false;
  });
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function readLeadType(form) {
  const select = form.querySelector('[data-contact-reason]');
  const reason = select instanceof HTMLSelectElement ? select.value.toLowerCase() : '';
  const match = LEAD_TYPE_BY_KEYWORD.find(([keyword]) => reason.includes(keyword));
  return match ? match[1] : LEAD_TYPE_FALLBACK;
}

/**
 * The Experience name carried by the request button, never typed by the visitor.
 * @param {HTMLFormElement} form
 * @param {string} leadType
 * @returns {string}
 */
function readExperienceName(form, leadType) {
  const input = form.querySelector('[data-contact-subject-input]');
  if (leadType !== 'experience' || !(input instanceof HTMLInputElement) || input.disabled) return '';
  return cleanName(input.value);
}

function trackLeads() {
  for (const form of document.querySelectorAll('form[data-contact-context]')) {
    if (!(form instanceof HTMLFormElement)) continue;

    if (form.querySelector('[data-contact-success]')) {
      if (readPending(PENDING_LEAD_KEY)) {
        whenPixelsReady().then(() => {
          // Read again: only the page that removes the data publishes it.
          const lead = readPending(PENDING_LEAD_KEY);
          if (!lead) return;
          clearPending(PENDING_LEAD_KEY);
          publish(EVENT_GENERATE_LEAD, lead);
        });
      }
    } else if (form.querySelector('[data-contact-error]')) {
      clearPending(PENDING_LEAD_KEY);
    }

    form.addEventListener('submit', () => {
      const leadType = readLeadType(form);
      storePending(
        PENDING_LEAD_KEY,
        withoutEmpty({
          lead_type: leadType,
          experience_name: readExperienceName(form, leadType),
          page_path: window.location.pathname,
        })
      );
    });
  }
}

function init() {
  trackCtaClicks(readCtaLabels());
  trackLeads();
}

init();
